import {
  MACHINE_I18N_PREFIX,
  MACHINE_KPI_SPNS,
  MACHINE_SIGNAL_COLUMNS,
  MACHINE_SIGNAL_GROUPS,
  MACHINE_SIGNALS_BY_SPN,
  MachineSignalDefinition,
  MachineSignalKind,
  MachineSignalSpn,
} from './machine-signal-catalog';
import {
  MACHINE_UPDATE_SUPPORTED_SCHEMA_VERSION,
  MachineDtcView,
  MachineFaultStatus,
  MachineFaultView,
  MachineLamp,
  MachineLampSeverity,
  MachineLampState,
  MachineLampView,
  MachineLinkState,
  MachineSignalState,
  MachineSignalView,
  MachineTelemetryView,
  MachineUpdateDtc,
  MachineUpdateFault,
  MachineUpdateMessage,
  MachineUpdateSignal,
  MachineUpdateSnapshot,
} from './machine-telemetry.model';

/**
 * The agroNave sends `machine_update` nominally every 10 s. After three missed
 * intervals the last section shown is flagged as outdated.
 *
 * `snapshot_age_ms` is deliberately not used for this: per the protocol, a large
 * snapshot age only means the machine state has not changed.
 */
export const MACHINE_UPDATE_OUTDATED_THRESHOLD_MS = 30000;

const FAULTS_PREFIX = `${MACHINE_I18N_PREFIX}.faults`;
const FMI_PREFIX = `${FAULTS_PREFIX}.fmi`;
const FMI_RESERVED_KEY = `${FMI_PREFIX}.reserved`;
const FMI_UNKNOWN_KEY = `${FMI_PREFIX}.unknown`;
/** FMIs 22 to 30 are reserved by SAE J1939-73; 31 is "condition exists". */
const FMI_LAST_DESCRIBED = 21;
const FMI_CONDITION_EXISTS = 31;

const SIGNAL_STATE_KEYS: Record<
  Exclude<MachineSignalState, MachineSignalState.Valid>,
  string
> = {
  [MachineSignalState.NeverReceived]: `${MACHINE_I18N_PREFIX}.signal-states.never-received`,
  [MachineSignalState.NotAvailable]: `${MACHINE_I18N_PREFIX}.signal-states.not-available`,
  [MachineSignalState.Error]: `${MACHINE_I18N_PREFIX}.signal-states.error`,
  [MachineSignalState.Stale]: `${MACHINE_I18N_PREFIX}.signal-states.stale`,
};

const LINK_KEYS: Record<MachineLinkState, string> = {
  [MachineLinkState.EngineNotDetected]: `${MACHINE_I18N_PREFIX}.link.engine-not-detected`,
  [MachineLinkState.Receiving]: `${MACHINE_I18N_PREFIX}.link.receiving`,
  [MachineLinkState.NoCommunication]: `${MACHINE_I18N_PREFIX}.link.no-communication`,
};

const FAULT_STATUS_KEYS: Record<MachineFaultStatus, string> = {
  [MachineFaultStatus.Active]: `${FAULTS_PREFIX}.status.active`,
  [MachineFaultStatus.NoActiveFaults]: `${FAULTS_PREFIX}.status.no-active-faults`,
  [MachineFaultStatus.Unknown]: `${FAULTS_PREFIX}.status.unknown`,
};

const LAMP_LABEL_KEYS: Record<MachineLamp, string> = {
  [MachineLamp.Mil]: `${FAULTS_PREFIX}.lamps.mil`,
  [MachineLamp.RedStop]: `${FAULTS_PREFIX}.lamps.red-stop`,
  [MachineLamp.Amber]: `${FAULTS_PREFIX}.lamps.amber`,
  [MachineLamp.Protect]: `${FAULTS_PREFIX}.lamps.protect`,
};

const LAMP_SHORT_LABEL_KEYS: Record<MachineLamp, string> = {
  [MachineLamp.Mil]: `${FAULTS_PREFIX}.lamps-short.mil`,
  [MachineLamp.RedStop]: `${FAULTS_PREFIX}.lamps-short.red-stop`,
  [MachineLamp.Amber]: `${FAULTS_PREFIX}.lamps-short.amber`,
  [MachineLamp.Protect]: `${FAULTS_PREFIX}.lamps-short.protect`,
};

const LAMP_STATE_KEYS: Record<MachineLampState, string> = {
  [MachineLampState.Off]: `${FAULTS_PREFIX}.lamp-states.off`,
  [MachineLampState.On]: `${FAULTS_PREFIX}.lamp-states.on`,
  [MachineLampState.SlowFlash]: `${FAULTS_PREFIX}.lamp-states.slow-flash`,
  [MachineLampState.FastFlash]: `${FAULTS_PREFIX}.lamp-states.fast-flash`,
  [MachineLampState.NotAvailable]: `${FAULTS_PREFIX}.lamp-states.not-available`,
  [MachineLampState.Unknown]: `${FAULTS_PREFIX}.lamp-states.unknown`,
};

/** Display order of the DM1 lamps, most severe first. */
const LAMP_ORDER: MachineLamp[] = [
  MachineLamp.RedStop,
  MachineLamp.Amber,
  MachineLamp.Protect,
  MachineLamp.Mil,
];

const LIT_LAMP_STATES = new Set<MachineLampState>([
  MachineLampState.On,
  MachineLampState.SlowFlash,
  MachineLampState.FastFlash,
]);

const FLASHING_LAMP_STATES = new Set<MachineLampState>([
  MachineLampState.SlowFlash,
  MachineLampState.FastFlash,
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function asArray<T>(value: T[] | undefined | null): T[] {
  if (Array.isArray(value)) {
    return value;
  }
  return [];
}

function isEnumValue<T extends string>(
  enumObject: Record<string, T>,
  value: unknown,
): value is T {
  return Object.values(enumObject).includes(value as T);
}

/**
 * Returns the `machine_update` section of a decoded `update` message, stamped with
 * the message's `device_update.timestamp`.
 *
 * @remarks The section is optional: it is only present on agroNaves with the machine
 * telemetry license, and even then in roughly every other `update`. An absent section
 * means "no new machine data", never "machine data is zero".
 */
export function extractMachineUpdate(
  decodedMessage: unknown,
): MachineUpdateSnapshot | undefined {
  if (
    !isRecord(decodedMessage) ||
    !isRecord(decodedMessage.machine_update) ||
    !isRecord(decodedMessage.device_update)
  ) {
    return undefined;
  }

  const receivedAt = decodedMessage.device_update.timestamp;
  if (!isFiniteNumber(receivedAt)) {
    return undefined;
  }

  return {
    message: decodedMessage.machine_update as unknown as MachineUpdateMessage,
    receivedAt,
  };
}

/**
 * Builds what the machine tab shows from the latest `machine_update` section.
 *
 * @param snapshot Latest section received and the instant it refers to.
 * @param latestDeviceTimestamp `device_update.timestamp` of the newest `update`
 * message, used to flag the section as outdated.
 *
 * @remarks Rules imposed by the protocol (schema version 1):
 * - An unknown `schema_version` is never decoded.
 * - A signal that is not `valid` never shows a number, not even its last valid value.
 * - Signals are identified by SPN and faults by source address, never by position.
 * - A fault code with `legacy_spn_conversion` never shows its SPN.
 */
export function buildMachineTelemetryView(
  snapshot: MachineUpdateSnapshot,
  latestDeviceTimestamp: number,
): MachineTelemetryView {
  const { message, receivedAt } = snapshot;
  const link = normalizeLink(message.link);
  const view: MachineTelemetryView = {
    isSupported:
      message.schema_version === MACHINE_UPDATE_SUPPORTED_SCHEMA_VERSION,
    schemaVersion: message.schema_version,
    link,
    linkKey: LINK_KEYS[link],
    groups: [],
    signalColumns: [],
    kpis: [],
    engineHours: null,
    signalCount: 0,
    unreadSignalCount: 0,
    faults: [],
    faultCards: [],
    healthyEcus: [],
    litLamps: [],
    activeDtcCount: 0,
    receivedAt,
    isOutdated:
      latestDeviceTimestamp - receivedAt > MACHINE_UPDATE_OUTDATED_THRESHOLD_MS,
  };

  if (!view.isSupported) {
    return view;
  }

  const signalsBySpn = new Map<number, MachineUpdateSignal>(
    asArray(message.signals)
      .filter((signal) => isRecord(signal))
      .map((signal) => [signal.spn, signal]),
  );
  view.groups = MACHINE_SIGNAL_GROUPS.map((group) => ({
    id: group.id,
    titleKey: group.titleKey,
    signals: group.signals.map((definition) =>
      decodeSignal(definition, signalsBySpn.get(definition.spn)),
    ),
  }));
  const groupsById = new Map(view.groups.map((group) => [group.id, group]));
  view.signalColumns = MACHINE_SIGNAL_COLUMNS.map((column) =>
    column.map((id) => groupsById.get(id)),
  );

  const signals = view.groups.flatMap((group) => group.signals);
  const signalsBySpnView = new Map(
    signals.map((signal) => [signal.spn, signal]),
  );
  view.kpis = MACHINE_KPI_SPNS.map((spn) => signalsBySpnView.get(spn));
  view.engineHours = signalsBySpnView.get(MachineSignalSpn.EngineHours);
  view.signalCount = signals.length;
  view.unreadSignalCount = signals.filter(
    (signal) => signal.state !== MachineSignalState.Valid,
  ).length;

  const engineAddress = message.engine?.source_address ?? null;
  view.faults = asArray(message.faults)
    .filter((fault) => isRecord(fault) && isFiniteNumber(fault.source_address))
    .map((fault) => buildFaultView(fault, engineAddress))
    .sort(compareFaults);
  view.faultCards = view.faults.filter(
    (fault) => fault.status !== MachineFaultStatus.NoActiveFaults,
  );
  view.healthyEcus = view.faults.filter(
    (fault) => fault.status === MachineFaultStatus.NoActiveFaults,
  );
  view.litLamps = collectLitLamps(view.faults);
  view.activeDtcCount = view.faults
    .filter((fault) => fault.status === MachineFaultStatus.Active)
    .reduce((total, fault) => total + fault.dtcCount, 0);

  return view;
}

/**
 * One entry per lamp lit on any ECU, in LAMP_ORDER (most severe first). When
 * several ECUs light the same lamp, the first one (the engine) is kept.
 */
function collectLitLamps(faults: MachineFaultView[]): MachineLampView[] {
  const lamps = faults.flatMap((fault) => fault.lamps);
  return LAMP_ORDER.map((lamp) =>
    lamps.find((view) => view.isLit && view.labelKey === LAMP_LABEL_KEYS[lamp]),
  ).filter((view) => view !== undefined);
}

/** Unknown link values are shown as a lost link, so stale data is never presented as live. */
function normalizeLink(link: unknown): MachineLinkState {
  if (isEnumValue(MachineLinkState, link)) {
    return link;
  }
  return MachineLinkState.NoCommunication;
}

function normalizeSignalState(
  definition: MachineSignalDefinition,
  signal: MachineUpdateSignal | undefined,
): MachineSignalState {
  if (signal === undefined) {
    return MachineSignalState.NeverReceived;
  }
  if (!isEnumValue(MachineSignalState, signal.state)) {
    return MachineSignalState.NotAvailable;
  }
  if (signal.state !== MachineSignalState.Valid) {
    return signal.state;
  }
  if (!isFiniteNumber(signal.raw)) {
    return MachineSignalState.NotAvailable;
  }
  if (
    definition.kind === MachineSignalKind.State &&
    definition.stateValueKeys[signal.raw] === undefined
  ) {
    return MachineSignalState.NotAvailable;
  }
  return MachineSignalState.Valid;
}

function decodeSignal(
  definition: MachineSignalDefinition,
  signal: MachineUpdateSignal | undefined,
): MachineSignalView {
  const signalState = normalizeSignalState(definition, signal);
  const view: MachineSignalView = {
    spn: definition.spn,
    labelKey: definition.labelKey,
    shortLabelKey: definition.shortLabelKey,
    kpiLabelKey: definition.kpiLabelKey,
    state: signalState,
    value: null,
    decimals: definition.decimals,
    unit: definition.unit,
    textKey: null,
  };

  if (signalState !== MachineSignalState.Valid) {
    view.textKey = SIGNAL_STATE_KEYS[signalState];
  } else if (definition.kind === MachineSignalKind.State) {
    view.textKey = definition.stateValueKeys[signal.raw];
  } else {
    view.value = signal.raw * definition.resolution + definition.offset;
  }

  return view;
}

function buildFaultView(
  fault: MachineUpdateFault,
  engineAddress: number | null,
): MachineFaultView {
  const dtcs = asArray(fault.dtcs).filter((dtc) => isRecord(dtc));
  let dtcTotal = dtcs.length;
  if (isFiniteNumber(fault.dtc_total)) {
    dtcTotal = fault.dtc_total;
  }
  let status = MachineFaultStatus.Unknown;
  if (isEnumValue(MachineFaultStatus, fault.status)) {
    status = fault.status;
  }

  return {
    sourceAddress: fault.source_address,
    isEngine: fault.source_address === engineAddress,
    status,
    statusKey: FAULT_STATUS_KEYS[status],
    lamps: LAMP_ORDER.map((lamp) => buildLampView(lamp, fault.lamps?.[lamp])),
    dtcs: dtcs.map(buildDtcView),
    hiddenDtcCount: Math.max(dtcTotal - dtcs.length, 0),
    dtcCount: Math.max(dtcTotal, dtcs.length),
  };
}

function buildLampView(lamp: MachineLamp, lampState: unknown): MachineLampView {
  let state = MachineLampState.Unknown;
  if (isEnumValue(MachineLampState, lampState)) {
    state = lampState;
  }

  return {
    labelKey: LAMP_LABEL_KEYS[lamp],
    shortLabelKey: LAMP_SHORT_LABEL_KEYS[lamp],
    stateKey: LAMP_STATE_KEYS[state],
    severity: getLampSeverity(lamp, state),
    isLit: LIT_LAMP_STATES.has(state),
    isFlashing: FLASHING_LAMP_STATES.has(state),
  };
}

function getLampSeverity(
  lamp: MachineLamp,
  state: MachineLampState,
): MachineLampSeverity {
  if (LIT_LAMP_STATES.has(state) && lamp === MachineLamp.RedStop) {
    return MachineLampSeverity.Danger;
  }
  if (LIT_LAMP_STATES.has(state)) {
    return MachineLampSeverity.Warning;
  }
  if (state === MachineLampState.Off) {
    return MachineLampSeverity.Ok;
  }
  return MachineLampSeverity.Muted;
}

/**
 * @remarks A code sent with `legacy_spn_conversion` never shows its SPN nor a catalog
 * signal name: the ECU used a legacy J1939-73 conversion method, so the bits do not
 * identify a parameter and could point at an unrelated signal. Its FMI and occurrence
 * count are still shown. Older payloads without the flag are read as standard SPNs.
 */
function buildDtcView(dtc: MachineUpdateDtc): MachineDtcView {
  let occurrenceCount: number | null = null;
  if (isFiniteNumber(dtc.occurrence_count)) {
    occurrenceCount = dtc.occurrence_count;
  }
  let spn: number | null = null;
  let signalLabelKey: string | null = null;
  if (dtc.legacy_spn_conversion !== true) {
    spn = dtc.spn;
    signalLabelKey = MACHINE_SIGNALS_BY_SPN.get(dtc.spn)?.labelKey ?? null;
  }

  return {
    spn,
    fmi: dtc.fmi,
    fmiKey: getFmiKey(dtc.fmi),
    signalLabelKey,
    occurrenceCount,
  };
}

function getFmiKey(fmi: number): string {
  if (!Number.isInteger(fmi) || fmi < 0 || fmi > FMI_CONDITION_EXISTS) {
    return FMI_UNKNOWN_KEY;
  }
  if (fmi > FMI_LAST_DESCRIBED && fmi < FMI_CONDITION_EXISTS) {
    return FMI_RESERVED_KEY;
  }
  return `${FMI_PREFIX}.${fmi}`;
}

/** Engine first, then by source address, so the cards keep their place between updates. */
function compareFaults(a: MachineFaultView, b: MachineFaultView): number {
  if (a.isEngine !== b.isEngine) {
    return Number(b.isEngine) - Number(a.isEngine);
  }
  return a.sourceAddress - b.sourceAddress;
}
